import { useCallback } from 'react'

import { faBars, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'antd'
import Title from 'antd/es/typography/Title'
import { HStack, Icon } from 'govwell-ui'
import styled from 'styled-components'

import { navigate, routes, useMatch } from '@redwoodjs/router'

import { OrgHomePageHeader } from 'src/constants'
import { useLightnessMode } from 'src/hooks/use-lightness-mode'

import { useAuth } from '../../auth'
import { BreakpointComponents } from '../../constants/breakpoints'
import { useCurrentBreakpoint } from '../../hooks/use-current-breakpoint'
import {
  useIsCitizen,
  useMyOrg,
  useMyUser,
  useOrganizationByPath,
} from '../../layouts/AppStateContextLayout/utils'
import LogInOutButton from '../LogInOutButton'
import { StylePropsMobile } from '../shared/StyledComponents'
import { Logo, LogoColor } from '../svgs/Logo'

import TopBarTemplate from './TopbarTemplate'

const Image = styled.img<StylePropsMobile>`
  width: auto;
  height: auto;
  max-width: 120px;
  max-height: 60px;
`

type Props = {
  onClickHamburger: () => void
  hamburgerOpen: boolean
  aiAssistantOnOpen?: () => void
  /** Renders left of the AI Assistant button (e.g. bulk job progress pill). */
  leadingChrome?: React.ReactNode
}

const TitleContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const TopBar = (props: Props) => {
  const { onClickHamburger, hamburgerOpen, aiAssistantOnOpen, leadingChrome } = props
  const { match: isOnLoginPage } = useMatch(routes.login())

  const { isSmall } = useCurrentBreakpoint()
  const small = isSmall(BreakpointComponents.Topbar)
  const { isAuthenticated } = useAuth()

  const orgSlugData = useOrganizationByPath()
  const myOrg = useMyOrg()
  const orgData = orgSlugData || myOrg

  const slug = orgData?.slug
  const logoUrl = orgData?.logoUrl

  const { isDarkMode } = useLightnessMode()
  const govWellLogoColor = isDarkMode ? LogoColor.White : LogoColor.Black

  const me = useMyUser()
  const isCitizen = useIsCitizen() || !me
  const onClickLogoOrTitle = () => {
    if (slug) {
      navigate(routes.cityHome({ slug }))
    } else {
      navigate(routes.home())
    }
  }

  let title: React.ReactNode = (
    <TitleContainer>
      <Title
        level={1}
        style={{
          margin: 0,
          width: '100%',
          textAlign: small ? 'left' : 'center',
          cursor: 'pointer',
          fontSize: '30px',
          lineHeight: 1.26,
        }}
        onClick={onClickLogoOrTitle}
        className="!text-neutral-950 dark:!text-neutral-50"
      >
        {orgData?.displayName || ''}
      </Title>
      {isCitizen && !small && (
        <Title level={4} className="!m-0 !text-neutral-800 dark:!text-neutral-200">
          {OrgHomePageHeader}
        </Title>
      )}
    </TitleContainer>
  )

  if (!orgData?.displayName) {
    title = null
  }

  const handleAIAssistantClick = useCallback(() => {
    aiAssistantOnOpen?.()
  }, [aiAssistantOnOpen])

  const aiButton = aiAssistantOnOpen ? (
    <Button
      onClick={handleAIAssistantClick}
      type="default"
      icon={<Icon.FaSparkles />}
      style={{ width: 'fit-content' }}
      className="hover:!border-[#FA541C] hover:!bg-[#FA541C] hover:!text-neutral-950"
    >
      {small ? 'AI' : 'AI Assistant'}
    </Button>
  ) : null

  const getButton = () => {
    if (isAuthenticated) {
      return (
        <HStack gap={2} align="center" width="fit-content">
          {leadingChrome}
          {aiButton}
          <Button
            onClick={onClickHamburger}
            type="primary"
            icon={<FontAwesomeIcon icon={hamburgerOpen ? faXmark : faBars} />}
          />
        </HStack>
      )
    }
    if (!isOnLoginPage) {
      return (
        <HStack gap={2} align="center" width="fit-content">
          {leadingChrome}
          {aiButton}
          <LogInOutButton />
        </HStack>
      )
    }
  }

  return (
    <TopBarTemplate
      onClickImage={onClickLogoOrTitle}
      image={
        logoUrl ? (
          <Image $small={small} src={logoUrl} alt={`Logo for ${orgData?.city ?? 'organization'}`} />
        ) : (
          <Logo color={govWellLogoColor} />
        )
      }
      title={title}
      button={getButton()}
    />
  )
}

export default TopBar
